/**
 * Remise, quantité offerte ou « offert » sur une ligne de prestation ou d'extra (2026-10-19, demande d'Emeni). Une ligne peut avoir :
 *  - des unités OFFERTES (`offered_quantity`, ou `discount_type: 'offered'` = toute la ligne),
 *  - puis une remise en % ou en euros sur ce qui reste payant.
 * Le calcul vit ICI, une seule fois, pour tous les écrans (création d'un rendez-vous, Dossier client, extras de la clôture) ;
 * le serveur refait le même calcul (chercher `lignesRemiseServeur` dans functions/api/[[path]].js) et fait autorité.
 */
export type TypeRemiseExtra = 'percent' | 'amount' | 'offered';

export interface ExtraAvecRemise {
    unit_price_ttc: number;
    quantity: number;
    discount_type?: TypeRemiseExtra | null;
    discount_value?: number;
    /** Nombre d'unités offertes sur la ligne (0 à quantité). */
    offered_quantity?: number;
}

const arrondi = (n: number) => Math.round(n * 100) / 100;

/** Unités offertes : toute la ligne pour « Offert », sinon la quantité saisie, bornée à la quantité de la ligne. */
export const quantiteOfferte = (e: ExtraAvecRemise): number => {
    const q = Number(e.quantity) || 1;
    if ((Number(e.unit_price_ttc) || 0) * q <= 0) return 0;
    if (e.discount_type === 'offered') return q;
    return Math.max(0, Math.min(q, Math.floor(Number(e.offered_quantity) || 0)));
};

export interface LigneRemise { label: string; montant: number }

/** Lignes de remise telles qu'elles figurent sur la facture : unités offertes, puis remise % / €. Montants positifs, TTC. */
export const lignesRemise = (e: ExtraAvecRemise & { label: string }): LigneRemise[] => {
    const q = Number(e.quantity) || 1;
    const u = Number(e.unit_price_ttc) || 0;
    const lignes: LigneRemise[] = [];
    const offerts = quantiteOfferte(e);
    if (offerts > 0) {
        lignes.push({
            label: offerts >= q ? `${e.label} — offert` : `${e.label} — ${offerts} offert${offerts > 1 ? 's' : ''}`,
            montant: arrondi(offerts * u),
        });
    }
    const base = (q - offerts) * u;
    if (base > 0 && (e.discount_type === 'percent' || e.discount_type === 'amount')) {
        const valeur = Math.max(0, Number(e.discount_value) || 0);
        const remise = e.discount_type === 'percent' ? arrondi(base * Math.min(100, valeur) / 100) : arrondi(Math.min(base, valeur));
        if (remise > 0) {
            lignes.push({
                label: e.discount_type === 'percent' ? `Remise ${e.label} : -${valeur}%` : `Remise ${e.label} : -${valeur.toFixed(2).replace('.', ',')} €`,
                montant: remise,
            });
        }
    }
    return lignes;
};

/** Montant TTC total retranché de la ligne (unités offertes + remise). */
export const montantRemiseExtra = (e: ExtraAvecRemise): number =>
    arrondi(lignesRemise({ ...e, label: '' }).reduce((somme, l) => somme + l.montant, 0));

/** Total TTC de la ligne après remises. */
export const totalLigneApresRemise = (e: ExtraAvecRemise): number =>
    arrondi((Number(e.unit_price_ttc) || 0) * (Number(e.quantity) || 1) - montantRemiseExtra(e));
